import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import type { AxiosError } from "axios";
import { IconMail, IconRefresh } from "@tabler/icons-react";

import MetaLogo from "@/assets/meta-logo.svg?react";
import TikTokIcon from "@/assets/tiktok";
import { BetaBadge } from "../tiktok/pre-release";
import PageHeader from "@/components/common/PageHeader";
import { StatusText } from "@/components/dashboard/dashboard-card";
import { Button } from "@/components/ui/button";
import { usePermissions } from "@/contexts/PermissionsContext";
import { getMetaAdsStatus } from "@/services/metaAds.service";
import { emailAutomationService } from "@/services/emailAutomation.service";
import { disconnectKlaviyo, getKlaviyoOauthUrl, getKlaviyoStatus } from "@/services/klaviyo.service";
import { disconnectOmnisend, getOmnisendOauthUrl, getOmnisendStatus } from "@/services/omnisend.service";
import {
  claimRechargeConnection,
  getRechargeStatus,
  RECHARGE_TICKET_KEY,
  type RechargeClaimErrorCode,
} from "@/services/recharge.service";
import { PlanFeature } from "@/types/permissions";
import { useTikTokAccess } from "@/hooks/useTikTokAccess";
import {
  disconnectTikTokShop,
  getTikTokShopOauthUrl,
  getTikTokShopStatus,
  type TikTokShopApp,
  type TikTokShopStatus,
} from "@/services/tiktokShop.service";

type Connection = "CONNECTED" | "NOT_CONNECTED" | "NEEDS_RECONNECT";

const STATUS: Record<Connection, { tone: string; label: string }> = {
  CONNECTED: { tone: "ACTIVE", label: "Connected" },
  NOT_CONNECTED: { tone: "INACTIVE", label: "Not connected" },
  NEEDS_RECONNECT: { tone: "SUSPENDED", label: "Needs reconnecting" },
};

interface CardProps {
  icon: ReactNode;
  name: string;
  description: string;
  /** null while loading, undefined when the status could not be read. */
  status?: Connection | null;
  planBadge?: string;
  footnote?: string;
  children: ReactNode;
}

function IntegrationCard({
  icon,
  name,
  description,
  status,
  planBadge,
  footnote,
  children,
}: CardProps) {
  return (
    <div
      id={`integration-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="relative flex flex-col rounded-xl border border-border bg-card p-5"
    >
      {/* PRE_RELEASE_TIKTOK: "Beta" wears the pilot badge; plan tiers keep the muted one. */}
      {planBadge === "Beta" ? (
        <BetaBadge floating />
      ) : planBadge ? (
        <span className="pointer-events-none absolute -right-2 -top-2 z-10 rounded-md border border-border bg-card px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground shadow-sm">
          {planBadge}
        </span>
      ) : null}
      <div className="flex items-start gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-border bg-background">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-foreground">{name}</h3>
          <div className="mt-0.5 text-xs">
            {status === null ? (
              <span className="text-muted-foreground">Checking…</span>
            ) : status ? (
              <StatusText status={STATUS[status].tone} label={STATUS[status].label} />
            ) : (
              <span className="text-muted-foreground">Status unavailable</span>
            )}
          </div>
        </div>
      </div>
      <p className="mt-3 flex-1 text-sm text-muted-foreground">{description}</p>
      {footnote && <p className="mt-2 text-xs text-muted-foreground">{footnote}</p>}
      <div className="mt-4 flex gap-2">{children}</div>
    </div>
  );
}

interface OAuthStatus {
  connected: boolean;
  status: "ACTIVE" | "NEEDS_RECONNECT" | "DISCONNECTED" | null;
  accountName: string | null;
}

interface OAuthProvider {
  /** Shown in toasts and the confirm dialog. */
  name: string;
  /** Matches ?<slug>_oauth=<result> on return and ?connect=<slug> from the app market. */
  slug: string;
  getStatus: () => Promise<OAuthStatus>;
  getOauthUrl: () => Promise<{ url: string }>;
  disconnect: () => Promise<unknown>;
}

const KLAVIYO: OAuthProvider = {
  name: "Klaviyo",
  slug: "klaviyo",
  getStatus: getKlaviyoStatus,
  getOauthUrl: getKlaviyoOauthUrl,
  disconnect: disconnectKlaviyo,
};

const OMNISEND: OAuthProvider = {
  name: "Omnisend",
  slug: "omnisend",
  getStatus: getOmnisendStatus,
  getOauthUrl: getOmnisendOauthUrl,
  disconnect: disconnectOmnisend,
};

/** Connect/disconnect state for an email tool that connects through OAuth. */
function useOAuthIntegration(provider: OAuthProvider) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [oauth, setOauth] = useState<OAuthStatus | null | undefined>(null);
  const [busy, setBusy] = useState(false);
  const { name, slug } = provider;

  const load = useCallback(() => {
    provider
      .getStatus()
      .then(setOauth)
      .catch(() => setOauth(undefined));
  }, [provider]);

  useEffect(() => load(), [load]);

  // The provider sends the brand back here with ?<slug>_oauth=<result>.
  useEffect(() => {
    const param = `${slug}_oauth`;
    const result = searchParams.get(param);
    if (!result) return;
    if (result === "success") {
      toast.success(`${name} connected`);
      load();
    } else if (result === "denied") {
      toast.error(`${name} authorization was cancelled`);
    } else {
      toast.error(`Could not connect ${name}`);
    }
    const next = new URLSearchParams(searchParams);
    next.delete(param);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, load, name, slug]);

  const connect = async () => {
    setBusy(true);
    try {
      const { url } = await provider.getOauthUrl();
      window.location.assign(url);
    } catch {
      toast.error(`Could not start the ${name} connection`);
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(`Disconnect ${name}? New affiliates will stop syncing to your ${name} account.`)) return;
    setBusy(true);
    try {
      await provider.disconnect();
      toast.success(`${name} disconnected`);
      load();
    } catch {
      toast.error(`Could not disconnect ${name}`);
    } finally {
      setBusy(false);
    }
  };

  // The app market's "Install" button sends brands to ?connect=<slug> (the
  // app's installation URL): start the connection once the status is known.
  useEffect(() => {
    if (searchParams.get("connect") !== slug || oauth === null) return;
    const next = new URLSearchParams(searchParams);
    next.delete("connect");
    setSearchParams(next, { replace: true });
    if (oauth?.connected) {
      toast.success(`${name} is already connected`);
    } else {
      void connect();
    }
    // connect is recreated each render; this should run once per visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, oauth]);

  const status: Connection | null | undefined =
    oauth === null || oauth === undefined
      ? oauth
      : oauth.connected
        ? "CONNECTED"
        : oauth.status === "NEEDS_RECONNECT"
          ? "NEEDS_RECONNECT"
          : "NOT_CONNECTED";

  return { status, accountName: oauth?.accountName ?? null, busy, connect, disconnect };
}

function OAuthButtons({ integration }: { integration: ReturnType<typeof useOAuthIntegration> }) {
  const { status, busy, connect, disconnect } = integration;
  return status === "CONNECTED" ? (
    <Button size="sm" variant="outline" disabled={busy} onClick={disconnect}>
      Disconnect
    </Button>
  ) : (
    <Button size="sm" disabled={busy || status === null} onClick={connect}>
      {status === "NEEDS_RECONNECT" ? "Reconnect" : "Connect"}
    </Button>
  );
}

function oauthFootnote(integration: ReturnType<typeof useOAuthIntegration>, keySaved: boolean, name: string) {
  if (integration.status === "CONNECTED" && integration.accountName) return `Account: ${integration.accountName}`;
  if (integration.status === "NOT_CONNECTED" && keySaved) {
    return `Syncing with a pasted API key. Connect to switch to a secure ${name} login.`;
  }
  return undefined;
}

/** Messages for ?tiktok_oauth=<result>, set by the backend callback. */
const TIKTOK_OAUTH_RESULTS: Record<string, { ok: boolean; message: string }> = {
  success: { ok: true, message: "TikTok Shop connected" },
  denied: { ok: false, message: "TikTok Shop authorization was cancelled" },
  shop_taken: { ok: false, message: "That TikTok Shop is already connected to another Reveshare brand" },
  unsupported_region: { ok: false, message: "Only United States TikTok Shops are supported for now" },
  retry: { ok: false, message: "TikTok didn't respond. Please try connecting again" },
};

const toConnection = (s: TikTokShopStatus[TikTokShopApp] | undefined): Connection =>
  s?.connected ? "CONNECTED" : s?.status === "NEEDS_RECONNECT" ? "NEEDS_RECONNECT" : "NOT_CONNECTED";

/**
 * One card for both TikTok apps: the affiliate app is the main connection;
 * the Analytics & Reporting app ("sales data") is offered once it is
 * configured on the server.
 *
 * PRE_RELEASE_TIKTOK: while the brand is in the pre-release only the
 * Analytics & Reporting app can be authorized, so the card shows that one
 * connection alone (the affiliate connect is hidden, not disabled: a button
 * that fails is worse than none). Collapse back to the two-app layout at GA.
 */
function TikTokShopCard({ allowed, loading }: { allowed: boolean; loading: boolean }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<TikTokShopStatus | null | undefined>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getTikTokShopStatus()
      .then(setStatus)
      .catch(() => setStatus(undefined));
  }, []);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  useEffect(() => {
    const result = searchParams.get("tiktok_oauth");
    if (!result) return;
    const known = TIKTOK_OAUTH_RESULTS[result];
    if (known?.ok) {
      toast.success(known.message);
      load();
    } else {
      toast.error(known?.message ?? "Could not connect TikTok Shop");
    }
    const next = new URLSearchParams(searchParams);
    next.delete("tiktok_oauth");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, load]);

  const connect = async (app: TikTokShopApp) => {
    setBusy(true);
    try {
      const { url } = await getTikTokShopOauthUrl(app);
      window.location.assign(url);
    } catch {
      toast.error("Could not start the TikTok Shop connection");
      setBusy(false);
    }
  };

  const disconnect = async (app: TikTokShopApp) => {
    const what = app === "data" ? "TikTok Shop sales data" : "TikTok Shop";
    if (!window.confirm(`Disconnect ${what}? Reveshare will stop reading from your TikTok Shop.`)) return;
    setBusy(true);
    try {
      await disconnectTikTokShop(app);
      toast.success(`${what} disconnected`);
      load();
    } catch {
      toast.error(`Could not disconnect ${what}`);
    } finally {
      setBusy(false);
    }
  };

  const affiliate = status?.affiliate;
  const data = status?.data;
  const preRelease = !!status?.preRelease;
  const primaryApp: TikTokShopApp = preRelease ? "data" : "affiliate";
  const primary = preRelease ? data : affiliate;
  const cardStatus: Connection | null | undefined = loading
    ? null
    : !allowed
      ? "NOT_CONNECTED"
      : status === null || status === undefined
        ? status
        : toConnection(primary);

  // The data app was authorized before the order scope was added to it:
  // a fresh authorization grants it and starts the order sync.
  const dataNeedsOrderScope = !!data?.connected && data.missingScopes.length > 0;

  let footnote: string | undefined;
  if (allowed && status && preRelease) {
    if (!data?.enabled) footnote = "Connecting is not available on this server yet.";
    else if (data.connected && data.shopName) footnote = `Shop: ${data.shopName}`;
    else if (cardStatus === "NEEDS_RECONNECT") footnote = "TikTok stopped accepting the connection. Reconnect to keep syncing.";
  } else if (allowed && status) {
    if (!affiliate?.enabled) footnote = "Connecting a TikTok Shop is coming soon.";
    else if (dataNeedsOrderScope)
      footnote = "Reconnect sales data to allow order access, so the dashboard can chart your actual TikTok orders.";
    else if (affiliate.connected && affiliate.shopName) footnote = `Shop: ${affiliate.shopName}`;
    else if (cardStatus === "NEEDS_RECONNECT") footnote = "TikTok stopped accepting the connection. Reconnect to keep syncing.";
  }

  return (
    <IntegrationCard
      icon={<TikTokIcon className="size-5 text-foreground" />}
      name="TikTok Shop"
      planBadge={preRelease ? "Beta" : "Scale"}
      description={
        preRelease
          ? "See your TikTok Shop sales next to Shopify in your dashboard. Creator tools unlock as TikTok approves them."
          : "See your creators' TikTok Shop sales next to their Shopify sales, and invite the TikTok creators already selling for you into your program."
      }
      status={cardStatus}
      footnote={footnote}
    >
      {loading ? null : !allowed ? (
        <Button size="sm" variant="outline" onClick={() => navigate("/brand/settings?tab=pricing")}>
          Upgrade to Scale
        </Button>
      ) : preRelease ? (
        !data?.enabled ? (
          <Button size="sm" disabled>
            Connect
          </Button>
        ) : cardStatus === "CONNECTED" ? (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => disconnect(primaryApp)}>
            Disconnect
          </Button>
        ) : (
          <Button size="sm" disabled={busy} onClick={() => connect(primaryApp)}>
            {cardStatus === "NEEDS_RECONNECT" ? "Reconnect" : "Connect"}
          </Button>
        )
      ) : !affiliate?.enabled ? (
        <Button size="sm" disabled>
          Connect
        </Button>
      ) : (
        <>
          {cardStatus === "CONNECTED" ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => disconnect("affiliate")}>
              Disconnect
            </Button>
          ) : (
            <Button size="sm" disabled={busy} onClick={() => connect("affiliate")}>
              {cardStatus === "NEEDS_RECONNECT" ? "Reconnect" : "Connect"}
            </Button>
          )}
          {cardStatus === "CONNECTED" &&
            data?.enabled &&
            (data.connected && dataNeedsOrderScope ? (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => connect("data")}>
                Reconnect sales data
              </Button>
            ) : data.connected ? (
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => disconnect("data")}>
                Disconnect sales data
              </Button>
            ) : (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => connect("data")}>
                {toConnection(data) === "NEEDS_RECONNECT" ? "Reconnect sales data" : "Connect sales data"}
              </Button>
            ))}
        </>
      )}
    </IntegrationCard>
  );
}

export default function Integrations() {
  const navigate = useNavigate();
  const { hasFeature, isLoading: permissionsLoading } = usePermissions();
  const [meta, setMeta] = useState<Connection | null | undefined>(null);
  const klaviyo = useOAuthIntegration(KLAVIYO);
  const omnisend = useOAuthIntegration(OMNISEND);
  const [klaviyoKeySaved, setKlaviyoKeySaved] = useState(false);
  const [omnisendKeySaved, setOmnisendKeySaved] = useState(false);
  const [recharge, setRecharge] = useState<Connection | null | undefined>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Each grant is claimed once, even though React StrictMode runs effects
  // twice in development and setSearchParams re-runs this one.
  const claimedGrant = useRef<string | null>(null);

  // Recharge sends the merchant back here after they approve (or not).
  // `claim` carries a sealed grant from the API; it is finished here, as the
  // logged-in brand, with the ticket RechargePage stored before leaving.
  useEffect(() => {
    const result = searchParams.get("recharge_oauth");
    if (!result) return;
    const grant = searchParams.get("recharge_grant");
    const next = new URLSearchParams(searchParams);
    next.delete("recharge_oauth");
    next.delete("recharge_grant");
    setSearchParams(next, { replace: true });

    if (result === "claim" && grant) {
      if (claimedGrant.current === grant) return;
      claimedGrant.current = grant;
      let ticket: string | null = null;
      try {
        ticket = sessionStorage.getItem(RECHARGE_TICKET_KEY);
      } catch {
        ticket = null;
      }
      const dropTicket = () => {
        try {
          sessionStorage.removeItem(RECHARGE_TICKET_KEY);
        } catch {
          /* nothing to drop */
        }
      };
      if (!ticket) {
        toast.error(
          "Start the Recharge connection from this page so Reveshare can confirm it is yours.",
        );
        return;
      }
      const claimMessages: Record<RechargeClaimErrorCode, string> = {
        TICKET_INVALID: "Could not confirm this connection was started here. Try again.",
        TICKET_EXPIRED: "That connection attempt expired. Click Connect again.",
        TICKET_BRAND_MISMATCH: "That connection was started by a different brand account.",
        GRANT_INVALID: "Recharge returned an incomplete response. Try again.",
        GRANT_EXPIRED: "That Recharge approval expired. Click Connect again.",
        STORE_MISMATCH: "The Recharge store you approved is not this brand's Shopify store.",
      };
      claimRechargeConnection({ grant, ticket })
        .then(() => {
          dropTicket();
          toast.success("Recharge connected");
          navigate("/brand/integrations/recharge", { replace: true });
        })
        .catch((e: AxiosError<{ code?: RechargeClaimErrorCode; message?: string }>) => {
          const code = e?.response?.data?.code;
          // A definitive refusal means this ticket is useless; a network blip
          // is not, so the brand can retry the return URL.
          if (code) dropTicket();
          toast.error(
            (code && claimMessages[code]) || e?.response?.data?.message || "Could not connect Recharge",
          );
        });
      return;
    }

    const messages: Record<string, string> = {
      denied: "Recharge authorization was denied",
      invalid_request: "Recharge returned an incomplete response",
      failed: "Could not connect Recharge",
    };
    toast.error(messages[result] ?? messages.failed);
  }, [searchParams, setSearchParams, navigate]);

  useEffect(() => {
    let cancelled = false;
    getMetaAdsStatus()
      .then((s) => {
        if (cancelled) return;
        setMeta(s.deauthorized ? "NEEDS_RECONNECT" : s.connected ? "CONNECTED" : "NOT_CONNECTED");
      })
      .catch(() => !cancelled && setMeta(undefined));
    getRechargeStatus()
      .then((s) => {
        if (cancelled) return;
        setRecharge(
          s.status === "NEEDS_RECONNECT" ? "NEEDS_RECONNECT" : s.connected ? "CONNECTED" : "NOT_CONNECTED",
        );
      })
      .catch(() => !cancelled && setRecharge(undefined));
    emailAutomationService
      .getEmailAutomation()
      .then((e) => {
        if (cancelled) return;
        setKlaviyoKeySaved(!!e?.klaviyoApiKey);
        setOmnisendKeySaved(!!e?.omnisendApiKey);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const tiktokAllowed = !permissionsLoading && hasFeature(PlanFeature.TIKTOK_SHOP);
  // PRE_RELEASE_TIKTOK: the card exists only for brands the server lets in.
  // At GA render it unconditionally again so other brands see the upsell.
  const tiktokAccess = useTikTokAccess();

  return (
    <div className="space-y-6 p-4">
      <PageHeader
        title="Integrations"
        description="Connect the tools you already use so Reveshare can share affiliate data with them."
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {tiktokAccess.enabled && <TikTokShopCard allowed={tiktokAllowed} loading={permissionsLoading} />}

        <IntegrationCard
          icon={<MetaLogo className="size-5" />}
          name="Meta Ads"
          description="Run creator content as Meta ads and credit affiliates for the sales they drive."
          status={meta}
        >
          <Button size="sm" variant="outline" onClick={() => navigate("/brand/meta-ads")}>
            {meta === "CONNECTED" ? "Manage" : "Set up"}
          </Button>
        </IntegrationCard>

        <IntegrationCard
          icon={<IconMail className="size-5 text-foreground" />}
          name="Klaviyo"
          description="Sync new affiliates to Klaviyo and send their joins, commissions, payouts and tier changes as events you can build flows on."
          status={klaviyo.status}
          footnote={oauthFootnote(klaviyo, klaviyoKeySaved, "Klaviyo")}
        >
          <OAuthButtons integration={klaviyo} />
        </IntegrationCard>

        <IntegrationCard
          icon={<IconMail className="size-5 text-foreground" />}
          name="Omnisend"
          description="Sync new affiliates to Omnisend and send their joins, commissions, payouts and tier changes as events you can build automations on."
          status={omnisend.status}
          footnote={oauthFootnote(omnisend, omnisendKeySaved, "Omnisend")}
        >
          <OAuthButtons integration={omnisend} />
        </IntegrationCard>

        <IntegrationCard
          icon={<IconRefresh className="size-5 text-foreground" />}
          name="Recharge"
          description="For stores selling subscriptions: pay affiliates on renewals, stop when the customer cancels, and see which affiliates bring subscribers who stay."
          status={recharge}
        >
          <Button size="sm" variant="outline" onClick={() => navigate("/brand/integrations/recharge")}>
            {recharge === "CONNECTED" ? "Manage" : "Set up"}
          </Button>
        </IntegrationCard>
      </div>
    </div>
  );
}
